import { RingProColors, RingSizes } from 'content/products/ring-pro/constants';

/** US 5-digit ZIP; ZIP+4 is cut to five digits before validation. */
export const US_ZIP_RE = /^\d{5}$/;

export const BB_PICKUP_ZIP_STORAGE_KEY = 'uh_bb_pickup_zip';

/** sessionStorage key of the parsed catalogue: {fetchedAt, skus[], variantIds{}}. */
export const BB_CATALOGUE_STORAGE_KEY = 'uh_bb_pickup_catalogue';

/** How long a catalogue is fresh: cache lifetime and refetch interval. */
export const BB_CATALOGUE_TTL_MS = 10 * 60_000;

/** Buy-page choice: {mode, store: {location_id, name, zip, sku}}. */
export const BB_PICKUP_STORE_STORAGE_KEY = 'uh_bb_pickup_store';

/** The live hold: {order_reference, expires_at, location_id, sku}. */
export const BB_PICKUP_HOLD_STORAGE_KEY = 'uh_bb_pickup_hold';

/** Cart line properties on the pickup ring line. Underscore keys are hidden
 * from the shopper in Shopify's checkout; all are allow-listed in
 * lib/commerce/cart/validateRequest.ts (keep the two lists in step). */
export const BB_LINE_PROPS = {
  LOCATION_ID: '_bb_pickup_location_id',
  STORE: '_bb_pickup_store',
  ZIP: '_bb_pickup_zip',
  SKU: '_bb_pickup_sku',
  /** The store's pickup date (YYYY-MM-DD, the store's own day) when the
   * shopper chose it, so the cart can say "Ready by". */
  DATE: '_bb_pickup_date',
} as const;

/** A YYYY-MM-DD date, as the backend sends pickup_date and promise_date. */
export const BB_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Written as a cart (order note) attribute AND as a line property: the
 * backend joins the paid order to the hold through it. */
export const BB_ORDER_REFERENCE_KEY = 'best_buy_order_reference';

/** Max length of an availability token we forward to the hold call. */
export const BB_MAX_TOKEN_LENGTH = 2048;

/** Stores shown before the "Show more" button. */
export const BB_PICKUP_VISIBLE_STORES = 5;

/** Stores the buy page lists before "Show all N stores" (Figma Flow V2). */
export const BB_BUY_PAGE_VISIBLE_STORES = 3;

/** Flat standard US shipping for a Ring PRO order (USD), shown on the buy
 * page's "To your door" card and order summary next to free pickup.
 * Confirmed by product 2026-10-27; keep in step with Shopify's US rate. */
export const US_STANDARD_SHIPPING_USD = 15;

/** Stores asked of every /locations lookup. The backend caps it at 25; one
 * lookup per full ZIP or locate keeps us far under its 20 a minute. */
export const BB_LOCATIONS_LIMIT = 25;

/**
 * Browser coordinates go to /locations, which measures from the point and
 * returns the ZIP. The browse SKU below is only for the /stores fallback while
 * the directory is building: size 08 Bionic Gold, the listing on bestbuy.com.
 */
export const BB_BROWSE_COLOR = RingProColors.GOLD;
export const BB_BROWSE_SIZE = RingSizes.EIGHT;

// Dev/offline fallback: while true, the pickup API calls answer from the
// sample payloads in sampleData.ts (no network call).
// Sample ZIP map: 99* answers "not offered", 00000 an invalid-ZIP error
// (/locations: not_found), 00001 a network-style error; anything else answers
// the sample stores.
// Normally OFF: previews reach the real API through the same-origin proxy
// routes (app/api/ring/best-buy-pickup/*), which backend recommended
// since api.ultrahuman.com's CORS allow-list only covers www.ultrahuman.com.
export const USE_SAMPLE_AVAILABILITY = false;

/** Whether the page is served from an ultrahuman.com host (production or
 * staging domains). There the browser calls api.ultrahuman.com directly
 * (origin on the API's CORS allow-list); everywhere else (Vercel previews,
 * localhost) the same-origin proxy route carries the call. */
export const isUltrahumanHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'ultrahuman.com' || host.endsWith('.ultrahuman.com');
};

// Hard safety: whatever the toggle says, a page served from an
// ultrahuman.com host always uses the live API, so fabricated availability
// can never reach real shoppers if this deploys with the toggle on.
export const sampleAvailabilityActive = (): boolean =>
  USE_SAMPLE_AVAILABILITY && !isUltrahumanHost();

/** Artificial latency for the sample answer, so the loading state shows. */
export const SAMPLE_LATENCY_MS = 300;

/** bestbuy.com Ring PRO product page. Every store name opens it; the link is
 * not per-store. */
export const BB_BRAND_PAGE_URL =
  'https://www.bestbuy.com/product/ultrahuman-ring-pro-smart-ring-size-before-you-buy-with-ring-pro-sizing-kit-no-subscription-needed-size-08-bionic-gold-2026/BLYXWLGGYS';

// Every event needs a GTM tag (GTM-MRTF5WC) before it reaches GA4; untagged
// dataLayer pushes drop silently. RESULT fires once per completed check.
export const BB_PICKUP_GTM_EVENTS = {
  ROW_CLICK: 'bbPickupRowClick',
  CHECK: 'bbPickupCheck',
  RESULT: 'bbPickupResult',
  STORE_SELECTED: 'bbPickupStoreSelected',
  DIRECTIONS_CLICK: 'bbPickupDirectionsClick',
  UBER_CLICK: 'bbPickupUberClick',
  BRAND_CLICK: 'bbPickupBrandClick',
  CHANGE: 'bbPickupChange',
  GEO: 'bbPickupGeoLocation',
  /** Buy page: the delivery choice rendered (exposure, once per page view). */
  SHOWN: 'bbPickupShown',
  /** Cart: checkout leaves with a pickup hold stamped. */
  CHECKOUT: 'bbPickupCheckout',
  /** Cart: checkout took other items out of a pickup cart first (the add
   * policy never saw them: an older cart, or another tab). */
  PRUNED: 'bbPickupCartPruned',
} as const;
